"use client";

import React, { useState } from "react";
import { MapPin, ArrowRight, Utensils } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const districts = [{
  id: "southeast-sector",
  name: "Southeast Sector",
  count: 12,
  description: "Primary operations area covering major airpark communities and regional hubs.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_southeast_sector.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_southeast_sector.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/hangar_southeast_sector.png-wvc-sizes",
    alt: "Aerial view of Southeast airpark community"
  },
  path: "M210,160 Q340,180 360,290 Q250,360 190,280 Z"
}, {
  id: "central-corridor",
  name: "Central Corridor",
  count: 8,
  description: "High-density aviation zone with specialized medical support and flight facilities.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_central_corridor.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_central_corridor.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/cockpit_central_corridor.png-wvc-sizes",
    alt: "Technical flight deck instrumentation"
  },
  path: "M40,140 Q150,180 190,280 Q80,350 30,250 Z"
}, {
  id: "coastal-approach",
  name: "Coastal Approach",
  count: 5,
  description: "Exclusive residential airparks with direct runway access and maritime climate.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_coastal_approach.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_coastal_approach.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/runway_coastal_approach.png-wvc-sizes",
    alt: "Runway at dawn coastal approach"
  },
  path: "M200,40 Q320,20 350,120 Q250,180 180,150 Z"
}, {
  id: "northern-terminal",
  name: "Northern Terminal",
  count: 4,
  description: "Expanding service area for retired ATP pilots in quiet, rural flight communities.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aircraft_northern_terminal.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aircraft_northern_terminal.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689271/img/aircraft_northern_terminal.png-wvc-sizes",
    alt: "Aircraft in cruise over northern terrain"
  },
  path: "M50,50 Q150,30 180,150 Q80,180 40,120 Z"
}];
function LatestPlacesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-64 rounded-md bg-muted/50 animate-pulse" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h3 className="font-default text-2xl font-bold text-foreground md:text-3xl uppercase tracking-tight">
            Flight Deck Updates
          </h3>
          <p className="mt-2 text-muted-foreground font-default">
            Recent operational briefings and care plan deployments.
          </p>
        </div>
        <Button variant="outline" className="hidden md:inline-flex border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground rounded-md" asChild>
          <Link to="/FlightDeckPage">View All Logs</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {posts.slice(0, 3).map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm transition-all hover:shadow-lg hover:shadow-primary/10 rounded-md">
            <div className="relative h-48 overflow-hidden">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[0.5] group-hover:grayscale-0" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted">
                  <Utensils className="h-8 w-8 text-muted-foreground/50" />
                </div>}
              <div className="absolute top-4 left-4">
                <Badge variant="default" className="bg-primary/90 backdrop-blur-md font-mono text-xs rounded-md">
                  ACTIVE
                </Badge>
              </div>
            </div>
            <CardHeader className="p-5 pb-0">
              <CardTitle className="line-clamp-1 text-lg font-default font-bold uppercase tracking-tight">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-3">
              <div className="flex items-center text-sm text-muted-foreground font-mono">
                <MapPin className="mr-1.5 h-4 w-4 shrink-0 text-primary" />
                <span className="line-clamp-1">
                  {post.customFields?.lokasi || "Sector 01-A"}
                </span>
              </div>
              {post.link && <Button variant="link" className="mt-4 h-auto p-0 text-primary font-default font-semibold" asChild>
                  <Link to={post.link}>
                    Review Briefing <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>}
            </CardContent>
          </Card>)}
      </div>
      <div className="mt-8 text-center md:hidden">
        <Button variant="outline" className="w-full border-secondary text-secondary rounded-md" asChild>
          <Link to="/FlightDeckPage">View All Logs</Link>
        </Button>
      </div>
    </div>;
}
export default function OurHangarLocation() {
  const [activeDistrict, setActiveDistrict] = useState<string | null>(null);
  const wp_query_params = {
    post_type: "kuliner",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="260035" id="our-hangar-location" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24">
      {}
      <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-10">
        <div className="h-[500px] w-[500px] rounded-md bg-primary/30 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-12 max-w-2xl">
          <Badge variant="secondary" className="mb-4 font-mono uppercase tracking-widest bg-secondary/20 text-secondary-foreground rounded-md">
            Station Info
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl uppercase">
            Our Hangar Location
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-default leading-relaxed">
            Strategic operational hubs designed to support aging pilots within their own airpark residences. Technical precision in care delivery across the Southeast.
          </p>
        </div>

        {}
        <div className="hidden md:grid md:grid-cols-12 md:gap-12 lg:gap-16 items-center">
          {}
          <div className="col-span-5 lg:col-span-4 flex flex-col gap-3">
            {districts.map((district, i) => <div key={district.id} data-index={i} className={`group cursor-pointer rounded-md border p-5 transition-all duration-300 ${activeDistrict === district.id ? "border-primary bg-primary/5 shadow-md shadow-primary/10" : "border-border/50 bg-card/30 hover:border-primary/30 hover:bg-card/80"}`} onMouseEnter={() => setActiveDistrict(district.id)} onMouseLeave={() => setActiveDistrict(null)}>
                <div className="flex items-center justify-between">
                  <h3 className={`font-default text-xl font-bold uppercase tracking-tight transition-colors ${activeDistrict === district.id ? "text-primary" : "text-foreground group-hover:text-primary"}`}>
                    {district.name}
                  </h3>
                  <Badge variant={activeDistrict === district.id ? "default" : "secondary"} className="transition-colors font-mono rounded-md">
                    {district.count} Units
                  </Badge>
                </div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2 font-default">
                  {district.description}
                </p>
              </div>)}
          </div>

          {}
          <div className="col-span-7 lg:col-span-8 relative">
            <div className="aspect-square w-full max-w-[600px] mx-auto relative rounded-md bg-secondary/10 p-8 border border-border/50 backdrop-blur-sm flex items-center justify-center">
              <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-2xl transition-all duration-500" style={{
              filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.2))"
            }}>
                {districts.map((district, i) => <path key={district.id} data-index={i} d={district.path} className={`transition-all duration-500 cursor-pointer stroke-[2px] ${activeDistrict === district.id ? "fill-primary stroke-primary/40 scale-[1.02] origin-center" : activeDistrict ? "fill-secondary/20 stroke-border/30 scale-95 origin-center opacity-40" : "fill-secondary/40 stroke-border hover:fill-primary/30 hover:stroke-primary/50"}`} onMouseEnter={() => setActiveDistrict(district.id)} onMouseLeave={() => setActiveDistrict(null)} />)}
              </svg>
              
              {}
              <div className={`absolute bottom-8 left-8 right-8 rounded-md bg-card/95 backdrop-blur-md p-4 border border-border shadow-xl transition-all duration-300 transform ${activeDistrict ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"}`}>
                {activeDistrict && <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-mono uppercase text-muted-foreground">Vectoring To</p>
                      <p className="font-default text-lg font-bold text-foreground uppercase tracking-tight">
                        {districts.find(d => d.id === activeDistrict)?.name}
                      </p>
                    </div>
                    <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" asChild>
                      <Link to="/TheFlightPlanPage">
                        View Sector <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {districts.map((district, i) => <Link key={district.id} to="/TheFlightPlanPage" className="block group">
              <div data-index={i} className="relative h-40 w-full overflow-hidden rounded-md">
                <img src={district.image.src} data-wvc-srcset={district.image.srcset} data-wvc-sizes={district.image.sizes} alt={district.image.alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale-[0.3]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="font-default text-xl font-bold text-white uppercase tracking-tight">
                        {district.name}
                      </h3>
                      <p className="mt-1 text-sm text-white/80 line-clamp-1 font-default">
                        {district.description}
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-white/10 text-white backdrop-blur-md border-white/20 hover:bg-white/20 rounded-md font-mono">
                      {district.count}
                    </Badge>
                  </div>
                </div>
              </div>
            </Link>)}
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "kuliner",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestPlacesConsumer />
        </PostsProvider>

      </div>
    </section>;
}